import React from "react";
import PropTypes from "prop-types";
import { Box, Grid, Card, CardContent, CardMedia, Typography, Button, IconButton, Stack, Chip } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";

const ConvocatoriaCardsGrid = ({ items = [], tipo = "convocatoria" }) => {
  if (!items.length) {
    return <Typography>No hay {tipo === "manual" ? "manuales" : "convocatorias"} disponibles.</Typography>;
  }

  return (
    tipo === "convocatoria" ? (
      <Box sx={{ width: "100%", minWidth: 0, py: 2 }}>
        <Stack spacing={2}>
          {items.map((item) => {
            const endDate = item.fechaFinalizacion ? new Date(item.fechaFinalizacion) : null;
            const normalizedStatus = String(item.status || "").toLowerCase().trim();
            const manuallyFinalized = normalizedStatus === "finalizada" || normalizedStatus === "finalizado";
            const isFinalizada = manuallyFinalized || (endDate && !Number.isNaN(endDate.getTime()) && endDate < new Date());
            const formatDate = (date) => date
              ? new Date(date).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" })
              : "";

            return (
              <Card
                key={item.id || item.slug || item.titulo}
                sx={{
                  display: "flex",
                  width: "100%",
                  minWidth: 0,
                  alignItems: "center",
                  gap: { xs: 1, sm: 2 },
                  px: { xs: 1.5, sm: 2.5 },
                  py: 2,
                  border: "1px solid #dce4ed",
                  borderRadius: 2,
                  boxShadow: isFinalizada ? "0 4px 18px rgba(211, 47, 47, 0.16)" : "0 2px 12px rgba(60, 72, 88, 0.06)",
                  backgroundColor: isFinalizada ? "#fffafa" : "#fff",
                }}
              >
                <Box sx={{ display: "grid", placeItems: "center", flexShrink: 0, width: 48, height: 48, borderRadius: 1.5, color: "#f44336", backgroundColor: "#fff0f0", border: "1px solid #ffd9d9", fontSize: 22 }}>
                  PDF
                </Box>
                <Box sx={{ minWidth: 0, flex: "1 1 auto", overflow: "hidden" }}>
                  <Typography sx={{ fontWeight: 700, color: "#0b1f33", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={item.titulo}>
                    {item.titulo}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.75, minWidth: 0 }}>
                    <CalendarTodayOutlinedIcon sx={{ fontSize: 19, color: "#54708d" }} />
                    <Typography variant="body2" color="text.secondary">
                      Registro: {formatDate(item.fechaInicio) || "Fecha no disponible"}{item.fechaFinalizacion ? ` al ${formatDate(item.fechaFinalizacion)}` : ""}
                    </Typography>
                    <Chip
                      label={isFinalizada ? "FINALIZADA" : "VIGENTE"}
                      size="small"
                      sx={{ height: 20, fontSize: 10, fontWeight: 800, letterSpacing: 0.4, color: isFinalizada ? "#c62828" : "#28704c", backgroundColor: isFinalizada ? "#ffe0e0" : "#e2f1e9" }}
                    />
                  </Stack>
                </Box>
                <IconButton component="a" href={`/noticias/${item.slug}`} aria-label={`Ver ${item.titulo}`} sx={{ color: "#7895b5", flexShrink: 0 }}>
                  <VisibilityOutlinedIcon />
                </IconButton>
                {item.pdf && (
                  <IconButton component="a" href={item.pdf} target="_blank" rel="noopener noreferrer" download aria-label={`Descargar ${item.titulo}`} sx={{ color: "#08233d", backgroundColor: "#f1f5f9", borderRadius: 1.5, flexShrink: 0 }}>
                    <DownloadOutlinedIcon />
                  </IconButton>
                )}
              </Card>
            );
          })}
        </Stack>
      </Box>
    ) : (
    <Box sx={{ width: "100%", py: 4, minHeight: '60vh', px: { xs: 1, md: 0 } }}>
      <Grid container spacing={4} justifyContent="center">
        {items.map((item, idx) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            key={idx}
            sx={{
              display: 'flex',
              mx: { xs: 1, sm: 0 },
            }}
          >
            <Card
              sx={{
                display: "flex",
                flexDirection: "column",
                width: '100%',
                height: '100%',
                borderRadius: 4,
                boxShadow: '0 4px 24px 0 rgba(60,72,88,0.13)',
                background: 'rgba(255,255,255,0.95)',
                overflow: 'hidden',
                transition: 'transform 0.18s cubic-bezier(.4,2,.6,1), box-shadow 0.18s',
                willChange: 'transform',
                '&:hover': {
                  transform: 'translateY(-10px) scale(1.03)',
                  boxShadow: '0 8px 32px 0 rgba(60,72,88,0.18)',
                },
              }}
            >
              {item.imagen && (
                <CardMedia
                  component="img"
                  image={item.imagen}
                  alt={item.titulo}
                  sx={{
                    width: '100%',
                    height: { xs: 180, sm: 200, md: 220 },
                    objectFit: 'cover',
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                    boxShadow: '0 2px 12px 0 rgba(60,72,88,0.10)',
                  }}
                />
              )}
              <CardContent sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                px: 2,
                pb: 3,
                pt: 2,
              }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                  {item.titulo}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                  {item.descripcion}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  href={tipo === "manual" ? (item.pdf || item.link) : ("/noticias/" + item.slug)}
                  target={tipo === "manual" ? "_blank" : undefined}
                  rel={tipo === "manual" ? "noopener noreferrer" : undefined}
                  sx={{
                    mt: 'auto',
                    borderRadius: 2,
                    fontWeight: 600,
                    boxShadow: 'none',
                    transition: 'background 0.2s, color 0.2s',
                    '&:hover': {
                      backgroundColor: 'highlight.main',
                      color: '#fff',
                    },
                  }}
                >
                  {tipo === "manual" ? 'Ver manual' : (item.textoDelBoton?.trim() || 'Ver más')}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
    )
  );
}



ConvocatoriaCardsGrid.propTypes = {
  items: PropTypes.array.isRequired,
  tipo: PropTypes.string
};

export default ConvocatoriaCardsGrid;
